import React, { useState, useEffect } from 'react';
import { post, put } from '../../api';
import './SuppliersForm.css';

const SuppliersForm = ({ supplier, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    primaryContact: {
      name: '',
      phone: '',
      email: ''
    },
    otherContacts: []
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const isEditing = !!supplier;

  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name || '',
        primaryContact: {
          name: supplier.primaryContact?.name || '',
          phone: supplier.primaryContact?.phone || '',
          email: supplier.primaryContact?.email || ''
        },
        otherContacts: supplier.otherContacts || []
      });
    }
  }, [supplier]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Supplier name is required';
    }

    if (formData.primaryContact.email && !isValidEmail(formData.primaryContact.email)) {
      newErrors['primaryContact.email'] = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        await put(`/contacts/suppliers/${supplier.supplierId}`, formData);
      } else {
        await post('/contacts/suppliers', formData);
      }

      const message = isEditing ? 'Supplier updated successfully' : 'Supplier created successfully';
      onSuccess(message);

    } catch (error) {
      console.error('Error saving supplier:', error);
      setErrors({ submit: error.response?.data?.error || error.message || 'Failed to save supplier' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const addOtherContact = () => {
    setFormData(prev => ({
      ...prev,
      otherContacts: [...prev.otherContacts, { name: '', phone: '', email: '' }]
    }));
  };

  const removeOtherContact = (index) => {
    setFormData(prev => ({
      ...prev,
      otherContacts: prev.otherContacts.filter((_, i) => i !== index)
    }));
  };

  const updateOtherContact = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      otherContacts: prev.otherContacts.map((contact, i) => 
        i === index ? { ...contact, [field]: value } : contact
      )
    }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Supplier' : 'Add New Supplier'}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="supplier-form">
          {errors.submit && (
            <div className="error-message">{errors.submit}</div>
          )}

          <div className="form-section">
            <h3>Basic Information</h3>
            
            <div className="form-group">
              <label htmlFor="name">
                Supplier Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={errors.name ? 'error' : ''}
                placeholder="Enter supplier name"
              />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>
          </div>

          <div className="form-section">
            <h3>Primary Contact</h3>
            
            <div className="form-group">
              <label htmlFor="contactName">Contact Name</label>
              <input
                type="text"
                id="contactName"
                value={formData.primaryContact.name}
                onChange={(e) => handleInputChange('primaryContact.name', e.target.value)}
                placeholder="Enter contact name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="contactPhone">Phone Number</label>
              <input
                type="tel"
                id="contactPhone"
                value={formData.primaryContact.phone}
                onChange={(e) => handleInputChange('primaryContact.phone', e.target.value)}
                placeholder="e.g., 0724 931504 or +254 724 931504"
              />
              <small className="field-help">Enter phone number in any format</small>
            </div>

            <div className="form-group">
              <label htmlFor="contactEmail">Email Address</label>
              <input
                type="email"
                id="contactEmail"
                value={formData.primaryContact.email}
                onChange={(e) => handleInputChange('primaryContact.email', e.target.value)}
                className={errors['primaryContact.email'] ? 'error' : ''}
                placeholder="Enter email address"
              />
              {errors['primaryContact.email'] && (
                <span className="field-error">{errors['primaryContact.email']}</span>
              )}
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h3>Additional Contacts</h3>
              <button 
                type="button" 
                className="btn btn-secondary btn-sm"
                onClick={addOtherContact}
              >
                Add Contact
              </button>
            </div>

            {formData.otherContacts.map((contact, index) => (
              <div key={index} className="other-contact">
                <div className="contact-header">
                  <span>Contact {index + 1}</span>
                  <button 
                    type="button"
                    className="remove-button"
                    onClick={() => removeOtherContact(index)}
                  >
                    Remove
                  </button>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={contact.name}
                      onChange={(e) => updateOtherContact(index, 'name', e.target.value)}
                      placeholder="Contact name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={contact.phone}
                      onChange={(e) => updateOtherContact(index, 'phone', e.target.value)}
                      placeholder="Phone number"
                    />
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={contact.email}
                      onChange={(e) => updateOtherContact(index, 'email', e.target.value)}
                      placeholder="Email address"
                    />
                  </div>
                </div>
              </div>
            ))}

            {formData.otherContacts.length === 0 && (
              <div className="empty-contacts">
                <p>No additional contacts added yet.</p>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEditing ? 'Update Supplier' : 'Create Supplier')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SuppliersForm;
